import { useState, useCallback, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, Building2, Loader2, Check } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface PappersCompany {
  siren: string;
  siret: string;
  nom_entreprise: string;
  denomination?: string;
  code_naf?: string;
  libelle_code_naf?: string;
  ville?: string;
  code_postal?: string;
  adresse?: string;
}

interface PappersCompanyDetail {
  siren: string;
  siret: string;
  nom_entreprise: string;
  denomination?: string;
  forme_juridique?: string;
  numero_tva_intracommunautaire?: string;
  capital_formate?: string;
  code_naf?: string;
  libelle_code_naf?: string;
  date_creation?: string;
  adresse?: string;
  code_postal?: string;
  ville?: string;
  numero_rcs?: string;
}

export interface PappersLookupResult {
  siren: string;
  siret: string;
  name: string;
  vatNumber: string;
  address?: string;
  postalCode?: string;
  city?: string;
  legalForm?: string;
}

interface PappersLookupProps {
  onSelect: (result: PappersLookupResult) => void;
  placeholder?: string;
  className?: string;
}

export function PappersLookup({ onSelect, placeholder = "Rechercher une entreprise (nom ou SIREN)...", className }: PappersLookupProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PappersCompany[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<string | null>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout>>();
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchCompanies = useCallback(async (searchQuery: string) => {
    if (searchQuery.trim().length < 2) {
      setResults([]);
      setShowResults(false);
      return;
    }

    setIsSearching(true);
    try {
      const { data, error } = await supabase.functions.invoke('pappers-lookup', {
        body: { action: 'search', query: searchQuery },
      });

      if (error) throw error;
      setResults(data.results || []);
      setShowResults(true);
    } catch (error: any) {
      console.error('Pappers search error:', error);
      // Don't show toast for minor search errors
    } finally {
      setIsSearching(false);
    }
  }, []);

  const handleInputChange = (value: string) => {
    setQuery(value);
    setSelectedCompany(null);

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      searchCompanies(value);
    }, 300);
  };

  const handleSelectCompany = async (company: PappersCompany) => {
    setShowResults(false);
    setIsLookingUp(true);
    setSelectedCompany(company.siren);
    setQuery(company.nom_entreprise || company.denomination || company.siren);

    try {
      // Do a full lookup to get the VAT number
      const { data, error } = await supabase.functions.invoke('pappers-lookup', {
        body: { action: 'lookup', siren: company.siren },
      });

      if (error) throw error;

      const detail: PappersCompanyDetail = data.company;
      
      onSelect({
        siren: detail.siren,
        siret: detail.siret,
        name: detail.nom_entreprise || detail.denomination || '',
        vatNumber: detail.numero_tva_intracommunautaire || '',
        address: detail.adresse,
        postalCode: detail.code_postal,
        city: detail.ville,
        legalForm: detail.forme_juridique,
      });

      if (detail.numero_tva_intracommunautaire) {
        toast.success(`N° TVA trouvé : ${detail.numero_tva_intracommunautaire}`);
      } else {
        toast.info('Aucun numéro de TVA trouvé pour cette entreprise');
      }
    } catch (error: any) {
      console.error('Pappers lookup error:', error);
      toast.error('Erreur lors de la recherche des informations entreprise');
    } finally {
      setIsLookingUp(false);
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className || ''}`}>
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => results.length > 0 && setShowResults(true)}
          placeholder={placeholder}
          className="pl-8 pr-8"
        />
        {(isSearching || isLookingUp) && (
          <Loader2 className="absolute right-2.5 top-2.5 h-4 w-4 animate-spin text-muted-foreground" />
        )}
        {selectedCompany && !isLookingUp && (
          <Check className="absolute right-2.5 top-2.5 h-4 w-4 text-green-600" />
        )}
      </div>
      
      {showResults && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-lg max-h-60 overflow-y-auto">
          {results.map((company) => (
            <button
              key={company.siren}
              type="button"
              onClick={() => handleSelectCompany(company)}
              className="flex w-full items-start gap-3 px-3 py-2 text-left hover:bg-accent transition-colors border-b last:border-b-0"
            >
              <Building2 className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">
                  {company.nom_entreprise || company.denomination}
                </p>
                <p className="text-xs text-muted-foreground">
                  SIREN: {company.siren}
                  {company.ville && ` · ${company.code_postal} ${company.ville}`}
                </p>
                {company.libelle_code_naf && (
                  <p className="text-xs text-muted-foreground truncate">{company.libelle_code_naf}</p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
      
      {showResults && results.length === 0 && !isSearching && query.length >= 2 && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-lg p-3">
          <p className="text-sm text-muted-foreground text-center">Aucune entreprise trouvée</p>
        </div>
      )}
    </div>
  );
}
